import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, onSnapshot, where } from 'firebase/firestore';
import { 
  IoArrowBack, 
  IoImageOutline, 
  IoWaterOutline, 
  IoFitnessOutline, 
  IoWineOutline, 
  IoSparklesOutline, 
  IoChevronForwardOutline 
} from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import './ReservarScreen.css'; // --- Premium CSS ---
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';

const getAmenidadIcon = (nombre = '') => {
  const n = nombre.toLowerCase();
  if (n.includes('alberca') || n.includes('piscina') || n.includes('swimming')) return <IoWaterOutline size={36} />;
  if (n.includes('gimnasio') || n.includes('gym') || n.includes('fitness')) return <IoFitnessOutline size={36} />;
  if (n.includes('fiesta') || n.includes('eventos') || n.includes('salon') || n.includes('bar')) return <IoWineOutline size={36} />;
  return <IoSparklesOutline size={36} />;
};

const AmenidadCard = ({ amenidad, onClick }) => {
  const tieneFoto = Boolean(amenidad.fotoUrl);

  return (
    <div className="amenity-card-modern" onClick={onClick}>
      <div className="amenity-image-wrapper">
        {tieneFoto ? (
          <img src={amenidad.fotoUrl} alt={amenidad.nombre} className="amenity-img" />
        ) : (
          <div className="amenity-icon-placeholder">
            {getAmenidadIcon(amenidad.nombre)}
          </div>
        )}
      </div>

      <div className="amenity-card-details">
        <div className="amenity-header-row">
          <h3 className="amenity-card-name">{amenidad.nombre}</h3>
          {amenidad.capacidadMax && (
            <span className="amenity-capacity-pill">
              Máx. {amenidad.capacidadMax} pers.
            </span>
          )}
        </div>

        <p className="amenity-card-desc">
          {amenidad.descripcion || 'Espacio común disponible para reserva de residentes.'}
        </p>

        <div className="amenity-card-action">
          <span>Ver disponibilidad</span>
          <IoChevronForwardOutline size={16} />
        </div>
      </div>
    </div>
  );
};

function ReservarScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [amenidades, setAmenidades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.condominioId) return;
    setLoading(true);
    
    // Traemos TODAS las amenidades del condominio, y filtramos en local.
    // Esto es mucho más seguro para lógicas compuestas (roles, tags múltiples)
    // considerando que rara vez hay más de 30 amenidades en un solo condominio.
    const q = query(
      collection(db, "amenidades"),
      where("condominioId", "==", userData.condominioId)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const listaTemp = [];
      const userTags = userData?.etiquetasAcceso || [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // 1. Omitir si está en mantenimiento
        if (data.bloqueadaPorAdmin) return;

        // 2. Filtro de Grupos de Acceso (Tags)
        const tagsAmenidad = data.derechosAcceso || [];
        // Compatibilidad con registros viejos: 'derechoAcceso': ['todos'] o string
        const viejaTags = data.derechoAcceso || []; 
        let tagsReales = [];
        
        if (tagsAmenidad.length > 0) {
            tagsReales = tagsAmenidad;
        } else if (Array.isArray(viejaTags)) {
            tagsReales = viejaTags;
        } else if (typeof viejaTags === 'string') {
            tagsReales = viejaTags.split(',').map(t => t.trim());
        }

        let tieneAcceso = false;
        if (tagsReales.length === 0) {
            tieneAcceso = true; // Amenidad Pública (sin tags configurados)
        } else if (tagsReales.some(t => typeof t === 'string' && t.toLowerCase() === 'todos')) {
            tieneAcceso = true; // Amenidad explícitamente pública
        } else {
            // Intersección: ¿Algún tag de la amenidad está en los tags del usuario?
            tieneAcceso = tagsReales.some(tag => {
                const tagLower = typeof tag === 'string' ? tag.toLowerCase().trim() : '';
                return userTags.some(uTag => typeof uTag === 'string' && uTag.toLowerCase().trim() === tagLower);
            });
        }

        if (tieneAcceso && !listaTemp.some(item => item.id === doc.id)) {
          listaTemp.push({ ...data, id: doc.id });
        }
      });

      // Asegurar nombres únicos visualmente (limpiar basura)
      const uniqueByName = Array.from(new Map(listaTemp.map(item => [item.nombre, item])).values());

      setAmenidades(uniqueByName);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar amenidades: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData]);

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/reservar" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>
        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Espacios para Ti</h1>
                <p>Reserva áreas comunes y disfruta.</p>
              </div>
              <IoImageOutline className="muro-hero-icon-decor" />
            </div>
          </div>
          <div className="reservar-container">

      {loading && <div className="empty-state">Cargando amenidades...</div>}

      {!loading && amenidades.length === 0 && (
        <div className="empty-state">
          <p>No hay amenidades disponibles exclusivas para ti en este momento.</p>
        </div>
      )}

      {/* --- Premium Grid --- */}
      {!loading && (
        <div className="amenities-grid">
          {amenidades.map(amenidad => (
            <AmenidadCard
              key={amenidad.id}
              amenidad={amenidad}
              onClick={() => navigate(`/reservar/${amenidad.id}`)}
            />
          ))}
        </div>
      )}
    </div>
        </div>

        {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
        <KondoBottomNav activeRoute="/reservar" />
      </main>
    </div>
  );
}

export default ReservarScreen;